// src/pages/Tickets/TicketStatusHistoryList.jsx
// -----------------------------------------------------------------------------
// Admin / PM screen for Ticket_Status_History_TBL.
// Full CRUD operations built with TableList, TableForm, DeleteConfirmModal,
// and custom hook useTableData.
// -----------------------------------------------------------------------------
import { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import ticketStatusHistoryService from '../../api/ticketStatusHistoryService';
import ticketStatusService from '../../api/ticketStatusService';
import ticketService, { mockStatuses } from '../../api/ticketService';
import userService from '../../api/userService';
import useTableData from '../../hooks/useTableData';
import TableList from '../../components/common/TableList';
import TableForm from '../../components/common/TableForm';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';
import StatusBadge from '../../components/common/StatusBadge';

// Fallback mock lists if services fail or return empty (offline support)
const FALLBACK_USERS = [
  { userID: 1, username: 'johndoe', firstName: 'John', lastName: 'Doe' },
  { userID: 2, username: 'janesmith', firstName: 'Jane', lastName: 'Smith' },
];

const FALLBACK_TICKETS = [
  { ticketID: 1, ticketNumber: 'TCK-001', title: 'Login Page Layout alignment issue', statusID: 1, statusName: 'Open' },
  { ticketID: 2, ticketNumber: 'TCK-002', title: 'Implement Dark Mode theme options', statusID: 2, statusName: 'In Progress' },
];

export default function TicketStatusHistoryList() {
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [statuses, setStatuses] = useState(mockStatuses);

  // Get current logged-in user ID
  const loggedInUserId = useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem('afix_user'));
      return u?.userID || u?.userId || u?.id || 1;
    } catch (e) {
      return 1;
    }
  }, []);

  // Initialize CRUD Hook
  const {
    data,
    paginatedData,
    loading,
    submitting,
    deleting,
    serverErrors,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalRecords,
    isFormOpen,
    isDeleteOpen,
    formMode,
    selectedRecord,
    openCreateModal,
    openEditModal,
    closeFormModal,
    openDeleteModal,
    closeDeleteModal,
    handleSave,
    handleDelete,
  } = useTableData(ticketStatusHistoryService, {
    idKey: 'historyID',
    itemName: 'Status History',
    initialSort: { key: 'historyID', direction: 'desc' },
  });

  // Load dropdown lists on mount
  useEffect(() => {
    ticketService
      .getAll()
      .then((res) => {
        const ticketList = res.data || [];
        setTickets(ticketList.length > 0 ? ticketList : FALLBACK_TICKETS);
      })
      .catch(() => setTickets(FALLBACK_TICKETS));

    ticketStatusService
      .getAll()
      .then((res) => {
        const statusList = res.data || [];
        if (statusList.length > 0) {
          setStatuses(statusList);
        }
      })
      .catch(() => setStatuses(mockStatuses));

    userService
      .getAll()
      .then((res) => {
        const userList = res.data?.data || res.data || [];
        setUsers(userList.length > 0 ? userList : FALLBACK_USERS);
      })
      .catch(() => setUsers(FALLBACK_USERS));
  }, []);

  // Form Fields Configuration
  const historyFields = useMemo(() => {
    const isEditMode = formMode === 'edit';
    const fields = [
      {
        name: 'ticketID',
        label: 'Ticket',
        type: 'select',
        required: true,
        options: tickets.map((t) => ({
          label: `${t.ticketNumber} — ${t.title} [Current: ${t.statusName || 'Open'}]`,
          value: t.ticketID,
        })),
      },
      {
        name: 'fromStatusID',
        label: 'From Status (Auto-detected)',
        type: 'select',
        required: true,
        readOnly: true,
        placeholder: 'Select a ticket to auto-detect current status',
        options: statuses.map((s) => ({
          label: s.statusName,
          value: s.statusID,
        })),
      },
      {
        name: 'toStatusID',
        label: 'New Status (To Status)',
        type: 'select',
        required: true,
        options: statuses.map((s) => ({
          label: s.statusName,
          value: s.statusID,
        })),
      },
      {
        name: 'changedBy',
        label: 'Changed By',
        type: 'select',
        required: true,
        defaultValue: loggedInUserId,
        options: users.map((u) => {
          const uID = u.id || u.userID || u.userId;
          const uFirstName = u.first_Name || u.firstName || '';
          const uLastName = u.last_Name || u.lastName || '';
          const fullName = `${uFirstName} ${uLastName}`.trim();
          return {
            label: fullName ? `${fullName} (${u.username || u.email || ''})` : (u.username || u.email || 'Unknown User'),
            value: uID,
          };
        }),
      },
    ];

    if (isEditMode) {
      fields.unshift({
        name: 'historyID',
        label: 'History ID',
        type: 'integer',
        isPrimaryKey: true,
        readOnly: true,
      });
      fields.push({
        name: 'changedDttm',
        label: 'Changed Date/Time',
        type: 'varchar',
        readOnly: true,
        placeholder: 'System Generated on update',
      });
    }

    return fields;
  }, [tickets, users, statuses, formMode, loggedInUserId]);

  // Handle cascading from-status auto population when ticket is selected
  const handleFormFieldChange = (fieldName, value, nextFormData) => {
    if (fieldName === 'ticketID') {
      const selectedTicket = tickets.find((t) => Number(t.ticketID) === Number(value));
      if (selectedTicket) {
        return {
          ...nextFormData,
          ticketID: Number(value),
          fromStatusID: Number(selectedTicket.statusID || 1),
        };
      }
    }
    return nextFormData;
  };

  // Custom Submit Handler: Save history AND automatically update the ticket's actual status
  const handleCustomSave = async (formData) => {
    try {
      // 1. Save history transition record
      await handleSave(formData);

      // 2. Automatically update the ticket's status in Ticket_TBL
      if (formData.ticketID && formData.toStatusID) {
        const currentTicket = tickets.find((t) => Number(t.ticketID) === Number(formData.ticketID));
        if (currentTicket) {
          await ticketService.update(formData.ticketID, {
            ...currentTicket,
            statusID: Number(formData.toStatusID),
          }).catch((err) => {
            console.warn('Could not update main ticket status:', err);
          });
        }
      }

      // 3. Refresh tickets list so current statuses are instantly updated
      const refreshed = await ticketService.getAll();
      setTickets(refreshed.data || []);

      const targetStatus = statuses.find((s) => Number(s.statusID) === Number(formData.toStatusID));
      toast.success(`Status history recorded & Ticket status updated to "${targetStatus?.statusName || 'New Status'}"!`);
    } catch (err) {
      console.error('Save status history error:', err);
    }
  };

  // Table Columns Definition
  const columns = [
    {
      key: 'historyID',
      label: 'History ID',
      sortable: true,
      width: '100px',
      render: (row) => row.historyID || row.historyId || row.id || '-',
    },
    {
      key: 'ticketID',
      label: 'Ticket',
      sortable: true,
      render: (row) => {
        const ticket = tickets.find((t) => Number(t.ticketID) === Number(row.ticketID || row.ticketId));
        return ticket ? (
          <div>
            <strong>{ticket.ticketNumber}</strong>
            <div style={{ fontSize: '11.5px', color: 'var(--color-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }}>
              {ticket.title}
            </div>
          </div>
        ) : (
          `ID: ${row.ticketID || row.ticketId}`
        );
      },
    },
    {
      key: 'fromStatusID',
      label: 'From Status',
      sortable: true,
      render: (row) => {
        const fromID = row.fromStatusID || row.fromStatusId;
        const status = statuses.find((s) => Number(s.statusID) === Number(fromID)) || mockStatuses.find((s) => Number(s.statusID) === Number(fromID));
        const statusText = row.fromStatusName || status?.statusName || (fromID ? `Status #${fromID}` : 'Open');
        return <StatusBadge value={statusText} />;
      },
    },
    {
      key: 'toStatusID',
      label: 'To Status',
      sortable: true,
      render: (row) => {
        const toID = row.toStatusID || row.toStatusId;
        const status = statuses.find((s) => Number(s.statusID) === Number(toID)) || mockStatuses.find((s) => Number(s.statusID) === Number(toID));
        const statusText = row.toStatusName || status?.statusName || (toID ? `Status #${toID}` : 'Unknown');
        return <StatusBadge value={statusText} />;
      },
    },
    {
      key: 'changedBy',
      label: 'Changed By',
      sortable: true,
      render: (row) => {
        const changerId = row.changedBy || row.userId;
        const user = users.find((u) => {
          const uID = u.id || u.userID || u.userId;
          return Number(uID) === Number(changerId);
        });
        if (user) {
          const uFirstName = user.first_Name || user.firstName || '';
          const uLastName = user.last_Name || user.lastName || '';
          const fullName = `${uFirstName} ${uLastName}`.trim();
          return fullName || user.username || user.email || `User ID: ${changerId}`;
        }
        return `User ID: ${changerId || 'System'}`;
      },
    },
    {
      key: 'changedDttm',
      label: 'Changed At',
      sortable: true,
      render: (row) => (row.changedDttm ? new Date(row.changedDttm).toLocaleString() : '-'),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Ticket Status History</h1>
          <div className="subtitle">
            Audit log tracking status transitions and workflow history for tickets.
          </div>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + Add Status Entry
        </button>
      </div>

      <TableList
        columns={columns}
        data={data}
        paginatedData={paginatedData}
        loading={loading}
        idKey="historyID"
        emptyMessage="No history logs found."
        // Search & Filter
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        // Sorting
        sortConfig={sortConfig}
        onSort={handleSort}
        // Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        // Actions
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />

      {/* Reusable Form Modal for Create & Edit */}
      {isFormOpen && (
        <TableForm
          fields={historyFields}
          initialValues={
            selectedRecord || {
              ticketID: '',
              fromStatusID: '',
              toStatusID: '',
              changedBy: loggedInUserId,
            }
          }
          mode={formMode}
          isOpen={isFormOpen}
          onFieldChange={handleFormFieldChange}
          onSubmit={handleCustomSave}
          onCancel={closeFormModal}
          submitting={submitting}
          serverErrors={serverErrors}
          itemName="Status Entry"
        />
      )}

      {/* Confirmation Delete Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Status History Record"
        itemLabel={selectedRecord ? `History ID: ${selectedRecord.historyID}` : 'Entry'}
        deleting={deleting}
      />
    </div>
  );
}
